import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Plus, Trash2, Info, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";

const RATINGS = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
const fmt = (n) => typeof n === "number" ? n.toLocaleString("en-US", { style: "currency", currency: "USD" }) : " ";

export default function Calculator() {
  useSeo({
    title: "VA Disability Pay Calculator | Veterans Disability Education",
    description: "Estimate your monthly VA disability compensation by rating and dependents. Free calculator from Veterans Disability Education.",
  });
  const [mode, setMode] = useState("known"); // 'known' | 'math'
  const [rating, setRating] = useState(70);
  const [married, setMarried] = useState(true);
  const [spouseAA, setSpouseAA] = useState(false);
  const [kidsUnder18, setKidsUnder18] = useState(2);
  const [kidsInSchool, setKidsInSchool] = useState(0);
  const [parents, setParents] = useState("0");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // VA Math state
  const [conditions, setConditions] = useState([50, 30]);
  const [mathResult, setMathResult] = useState(null);

  const compute = async (r = rating) => {
    setLoading(true);
    try {
      const { data } = await api.post("/calculator/compensation", {
        rating: r, spouse: married, spouse_aa: spouseAA,
        kids_under_18: parseInt(kidsUnder18) || 0,
        kids_in_school: parseInt(kidsInSchool) || 0,
        parents: parseInt(parents) || 0, year: 2026,
      });
      setResult(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { compute(rating); /* eslint-disable-next-line */ }, [rating, married, spouseAA, kidsUnder18, kidsInSchool, parents]);

  const computeCombined = async () => {
    const clean = conditions.map((c) => parseInt(c) || 0).filter((c) => c > 0);
    if (clean.length === 0) return;
    const { data } = await api.post("/calculator/combined", { ratings: clean });
    setMathResult(data);
    if (data.rounded > 0) {
      setRating(data.rounded);
      compute(data.rounded);
    }
  };
  useEffect(() => { computeCombined(); /* eslint-disable-next-line */ }, [conditions]);

  const addRow = () => setConditions([...conditions, 10]);
  const removeRow = (i) => setConditions(conditions.filter((_, idx) => idx !== i));
  const setRow = (i, v) => {
    const next = [...conditions];
    next[i] = Math.max(0, Math.min(100, parseInt(v) || 0));
    setConditions(next);
  };

  const breakdown = useMemo(() => Object.entries(result?.breakdown || {}), [result]);

  return (
    <div>
      <section className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-14">
          <div className="label-caps text-accent mb-4">2026 VA Compensation Calculator</div>
          <h1 className="font-display text-4xl lg:text-6xl leading-[1.02] max-w-3xl">
            What can you actually <span className="text-accent">bring home?</span>
          </h1>
          <p className="mt-5 text-lg text-primary-foreground/85 max-w-2xl">
            Live monthly and annual estimate using 2026 VA compensation rates (effective Dec 1, 2025, 2.8% COLA increase).
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-10">
        {/* MODE TOGGLE */}
        <div className="inline-flex bg-secondary border-2 border-primary p-1 mb-10" data-testid="mode-toggle">
          <button
            className={`px-6 py-3 font-semibold text-[15px] ${mode === "known" ? "bg-primary text-primary-foreground" : "text-primary hover:bg-white"}`}
            onClick={() => setMode("known")} data-testid="mode-known"
          >
            I Know My Combined Rating
          </button>
          <button
            className={`px-6 py-3 font-semibold text-[15px] ${mode === "math" ? "bg-primary text-primary-foreground" : "text-primary hover:bg-white"}`}
            onClick={() => setMode("math")} data-testid="mode-math"
          >
            Calculate My Combined Rating (VA Math)
          </button>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* INPUTS */}
          <div className="lg:col-span-7">
            {mode === "math" && (
              <Card className="p-8 border-2 mb-6" data-testid="va-math-card">
                <div className="label-caps mb-2">Step 1, Add each condition</div>
                <h2 className="font-display text-2xl mb-6">Your service-connected conditions</h2>

                <div className="space-y-3">
                  {conditions.map((c, i) => (
                    <div key={i} className="flex items-center gap-3" data-testid={`condition-row-${i}`}>
                      <Label className="w-28 text-primary/70">Condition {i + 1}</Label>
                      <Select value={String(c)} onValueChange={(v) => setRow(i, v)}>
                        <SelectTrigger className="w-32 h-12" data-testid={`condition-select-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {RATINGS.map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <button className="text-primary/60 hover:text-destructive p-2" onClick={() => removeRow(i)} disabled={conditions.length <= 1} data-testid={`condition-remove-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>

                <Button variant="outline" size="sm" className="mt-4" onClick={addRow} data-testid="condition-add">
                  <Plus className="w-4 h-4" /> Add condition
                </Button>

                {mathResult && (
                  <div className="mt-8 bg-secondary p-6 border-l-4 border-accent" data-testid="va-math-result">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <div className="label-caps text-primary/70">Exact combined</div>
                        <div className="font-display text-4xl mt-1" data-testid="math-exact">{mathResult.exact}%</div>
                      </div>
                      <div>
                        <div className="label-caps text-primary/70">Rounded rating</div>
                        <div className="font-display text-4xl mt-1 text-primary" data-testid="math-rounded">{mathResult.rounded}%</div>
                      </div>
                    </div>
                    <p className="mt-5 text-sm text-primary/70 leading-relaxed">
                      The VA rounds combined values to the nearest 10 (values ending in 5 round up). Your dependent amounts below use the rounded rating.
                    </p>
                  </div>
                )}

                <p className="mt-6 text-xs text-primary/60 border-t border-border pt-4 leading-relaxed">
                  <Info className="w-3.5 h-3.5 inline mr-1 text-accent" />
                  Bilateral conditions (paired arms or legs) may increase your combined rating. Book a consult to review your specific ratings.
                </p>
              </Card>
            )}

            <Card className="p-8 border-2" data-testid="rating-card">
              <div className="label-caps mb-2">{mode === "math" ? "Step 2, Add dependents" : "Your rating & dependents"}</div>
              <h2 className="font-display text-2xl mb-6">Compensation inputs</h2>

              <div className="space-y-7">
                {mode === "known" && (
                  <div>
                    <Label className="text-base">Combined rating</Label>
                    <div className="mt-3 grid grid-cols-5 gap-2" data-testid="rating-grid">
                      {RATINGS.map((r) => (
                        <button key={r} onClick={() => setRating(r)}
                          className={`h-12 font-semibold border-2 ${rating === r ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border hover:border-primary"}`}
                          data-testid={`rating-${r}`}>
                          {r}%
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="border-t border-border pt-6">
                  <Label className="text-base mb-3 block">Marital status</Label>
                  <RadioGroup value={married ? "married" : "single"} onValueChange={(v) => setMarried(v === "married")} className="flex gap-3">
                    {["single", "married"].map((v) => (
                      <label key={v} className={`px-5 h-12 inline-flex items-center border-2 cursor-pointer font-semibold ${married === (v === "married") ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border hover:border-primary"}`}>
                        <RadioGroupItem value={v} className="sr-only" data-testid={`marital-${v}`} />
                        {v[0].toUpperCase() + v.slice(1)}
                      </label>
                    ))}
                  </RadioGroup>
                </div>

                {married && (
                  <div className="flex items-center justify-between border-t border-border pt-6">
                    <div>
                      <Label className="text-base">Spouse Aid & Attendance</Label>
                      <p className="text-sm text-primary/60 mt-1">Additional add-on if spouse qualifies medically</p>
                    </div>
                    <Switch checked={spouseAA} onCheckedChange={setSpouseAA} data-testid="spouse-aa" />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 border-t border-border pt-6">
                  <div>
                    <Label>Children under 18</Label>
                    <Input type="number" min="0" value={kidsUnder18} onChange={(e) => setKidsUnder18(e.target.value)} className="mt-2 h-12" data-testid="kids-under-18" />
                  </div>
                  <div>
                    <Label>Children 18–23 in qualifying school</Label>
                    <Input type="number" min="0" value={kidsInSchool} onChange={(e) => setKidsInSchool(e.target.value)} className="mt-2 h-12" data-testid="kids-in-school" />
                  </div>
                </div>

                <div className="border-t border-border pt-6">
                  <Label>Dependent parents</Label>
                  <Select value={parents} onValueChange={setParents}>
                    <SelectTrigger className="mt-2 h-12" data-testid="parents-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">None</SelectItem>
                      <SelectItem value="1">One parent</SelectItem>
                      <SelectItem value="2">Two parents</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {rating < 30 && (
                  <div className="bg-secondary p-4 border-l-4 border-accent text-sm">
                    <Info className="w-4 h-4 inline mr-2 text-primary" />
                    At {rating}%, dependents don't change your compensation. Dependent add-ons start at 30%.
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* RESULT */}
          <div className="lg:col-span-5">
            <Card className="p-8 bg-primary text-primary-foreground border-0 sticky top-24" data-testid="result-card">
              <div className="label-caps text-accent">Estimated monthly (tax-free)</div>
              <div className="font-display text-6xl mt-3" data-testid="result-monthly">
                {loading ? <Loader2 className="w-10 h-10 animate-spin" /> : fmt(result?.monthly)}
              </div>
              <div className="mt-2 text-primary-foreground/80">
                <span data-testid="result-annual">{fmt(result?.annual)}</span> per year
              </div>

              <div className="my-6 h-px bg-primary-foreground/20" />

              <div className="label-caps text-accent mb-3">Breakdown</div>
              <ul className="space-y-2 text-[15px]">
                {breakdown.map(([k, v]) => (
                  <li key={k} className="flex items-center justify-between">
                    <span className="text-primary-foreground/85 capitalize">{k.replace(/_/g, " ")}</span>
                    <span className="font-mono">{fmt(v)}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6 pt-6 border-t border-primary-foreground/20 text-xs text-primary-foreground/70 leading-relaxed">
                Estimates based on published VA compensation rates. Your actual payment is determined by the VA. This tool is for education only.
              </div>
            </Card>
          </div>
        </div>

        {/* P&T Explainer */}
        <div className="mt-14 bg-secondary p-6 lg:p-10">
          <Accordion type="single" collapsible defaultValue="pt">
            <AccordionItem value="pt" className="border-0">
              <AccordionTrigger className="hover:no-underline" data-testid="pt-explainer">
                <div className="text-left">
                  <div className="label-caps text-primary/70">Bonus explainer</div>
                  <h3 className="font-display text-2xl lg:text-3xl mt-1">What does 100% Permanent and Total (P&T) mean?</h3>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-[16px] leading-relaxed space-y-4 text-primary/90 pt-4">
                <p>100% Permanent and Total (P&T) means the VA considers your service-connected conditions <strong>total</strong> AND <strong>not expected to improve.</strong> A P&T rating unlocks benefits far beyond monthly compensation:</p>
                <ul className="space-y-2 pl-6 list-disc">
                  <li><strong>Protection from routine future exams</strong>, no more reduction anxiety.</li>
                  <li><strong>CHAMPVA</strong> health coverage for your spouse and eligible children.</li>
                  <li><strong>Chapter 35 (DEA)</strong> education benefits for dependents.</li>
                  <li><strong>State-level benefits</strong>, property tax exemptions, in-state tuition, hunting/fishing licenses, DMV fees (varies by state).</li>
                </ul>
                <p>P&T is <em>not</em> automatic at 100%. Your rating decision must explicitly say "permanent" or state "no future exams are scheduled." If your 100% letter doesn't say permanent, you can request that determination.</p>
                <div className="pt-4">
                  <Button asChild><Link to="/intake">Book a consult about P&T →</Link></Button>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    </div>
  );
}
